import { formatHrBookingRef } from "../../utils/bookingRefDisplay.js";

/**
 * נרמול טוקן חיפוש / שדה הזמנה: lowercase, trim, ללא רווחים/מקפים, ללא prefix HR.
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeSearchToken(value) {
  let s = String(value ?? "").trim().toLowerCase();
  if (!s) return "";
  s = s.replace(/\s+/g, "");
  s = s.replace(/-/g, "");
  if (s.startsWith("hr")) {
    s = s.slice(2);
  }
  return s;
}

function safeParseOrderResponse(raw) {
  const or = raw?.order_response ?? raw?.orderResponse;
  if (or == null) return null;
  if (typeof or === "object" && !Array.isArray(or)) return or;
  if (typeof or === "string" && or.trim().startsWith("{")) {
    try {
      const p = JSON.parse(or);
      return p && typeof p === "object" ? p : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** @param {unknown} raw @returns {string[]} */
function bookingRefTokenVariants(raw) {
  if (raw == null) return [];
  const s = String(raw).trim();
  if (!s) return [];
  const out = new Set();
  out.add(normalizeSearchToken(s));
  const digits = s.replace(/\D/g, "");
  if (digits.length >= 6) {
    out.add(normalizeSearchToken(digits));
    if (digits.length === 8) {
      out.add(normalizeSearchToken(`HR-${digits}`));
    }
  }
  const hr = formatHrBookingRef(digits.length === 8 ? digits : null);
  if (hr) out.add(normalizeSearchToken(hr));
  return [...out].filter(Boolean);
}

/**
 * @param {Record<string, unknown>} o — שורה מנורמלת (normalizeOrders)
 * @returns {string} מחרוזת מאוחדת של כל הטוקנים לחיפוש substring
 */
export function buildOrderSearchHaystack(o) {
  if (!o || typeof o !== "object") return "";

  const raw = o.raw && typeof o.raw === "object" ? o.raw : null;
  const bookingRefRaw = o.bookingRef ?? raw?.booking_ref ?? raw?.bookingRef ?? null;
  const hrFormatted = formatHrBookingRef(bookingRefRaw);

  const orParsed = raw ? safeParseOrderResponse(raw) : null;
  const resv = orParsed?.reservation && typeof orParsed.reservation === "object" ? orParsed.reservation : null;

  const parts = [
    o.orderId,
    o.id,
    o.displayOrderNumber,
    bookingRefRaw,
    hrFormatted,
    o.mainContact,
    o.hotelName,
    o.cityOrDestination,
    o.category,
    o.status,
    raw?.proposalNumber,
    raw?.proposal_number,
    raw?.dealid,
    raw?.dealId,
    raw?.orderid,
    raw?.orderId,
    orParsed?.proposalNumber,
    orParsed?.proposal_number,
    orParsed?.supplierReservationNumber,
    orParsed?.supplier_reservation_number,
    orParsed?.reservationNumber,
    orParsed?.bookingRef,
    orParsed?.booking_ref,
    resv?.supplierPNR,
    resv?.supplier_pnr,
    resv?.bookingRef,
    resv?.booking_ref,
    resv?.BookingRef,
  ];

  for (const v of bookingRefTokenVariants(bookingRefRaw)) {
    parts.push(v);
  }
  for (const v of bookingRefTokenVariants(o.displayOrderNumber)) {
    parts.push(v);
  }

  const tokens = parts.map(normalizeSearchToken).filter(Boolean);
  return [...new Set(tokens)].join("|");
}

/**
 * @param {Record<string, unknown>} o
 * @param {string} needle — כבר מנורמל
 * @returns {{ matched: boolean, matchedField?: string, matchedToken?: string }}
 */
export function traceOrderSearchMatch(o, needle) {
  if (!needle) return { matched: true, matchedField: "empty_query" };

  const bookingRaw = o?.bookingRef ?? o?.raw?.booking_ref ?? o?.raw?.bookingRef;
  const orParsed = o?.raw ? safeParseOrderResponse(o.raw) : null;
  const resv = orParsed?.reservation && typeof orParsed.reservation === "object" ? orParsed.reservation : null;

  const fieldChecks = [
    ["displayOrderNumber", o?.displayOrderNumber],
    ["bookingRef", bookingRaw],
    ["bookingRefHr", formatHrBookingRef(bookingRaw)],
    ["rawBookingRef", o?.raw?.booking_ref],
    ["orderResponseBookingRef", orParsed?.bookingRef ?? orParsed?.booking_ref],
    ["reservationBookingRef", resv?.bookingRef ?? resv?.booking_ref ?? resv?.BookingRef],
    ["orderId", o?.orderId],
    ["id", o?.id],
    ["mainContact", o?.mainContact],
    ["hotelName", o?.hotelName],
    ["cityOrDestination", o?.cityOrDestination],
    ["category", o?.category],
    ["offerNumber", o?.raw?.proposalNumber ?? o?.raw?.proposal_number],
  ];

  for (const [field, val] of fieldChecks) {
    for (const tok of bookingRefTokenVariants(val)) {
      if (!tok) continue;
      if (tok === needle || tok.includes(needle) || (needle.length >= 6 && needle.includes(tok) && tok.length >= 6)) {
        return { matched: true, matchedField: field, matchedToken: tok };
      }
    }
    const tok = normalizeSearchToken(val);
    if (!tok) continue;
    if (tok === needle || tok.includes(needle) || (needle.length >= 6 && needle.includes(tok) && tok.length >= 6)) {
      return { matched: true, matchedField: field, matchedToken: tok };
    }
  }

  const hay = buildOrderSearchHaystack(o);
  if (hay.includes(needle)) {
    return { matched: true, matchedField: "haystack", matchedToken: hay.slice(0, 120) };
  }

  if (/^\d{6,}$/.test(needle)) {
    const hayDigits = hay.replace(/\D/g, "");
    if (hayDigits.includes(needle)) {
      return { matched: true, matchedField: "haystack_digits", matchedToken: needle };
    }
  }

  return { matched: false };
}

/**
 * @param {Record<string, unknown>} o
 * @param {string} normalizedNeedle
 */
export function orderMatchesOperationRequestQuery(o, normalizedNeedle) {
  return traceOrderSearchMatch(o, normalizedNeedle).matched;
}

/** @param {Record<string, unknown>} o */
export function isOperationRequestCancelledOrder(o) {
  const st = String(o?.status ?? "").toLowerCase();
  if (st.includes("בוטל") || st.includes("cancel")) return true;
  const raw = o?.raw;
  if (raw && typeof raw === "object" && (raw.cancelled === true || raw.cancelled === "true")) {
    return true;
  }
  return false;
}
